if (!customElements.get('cart-drawer-complete-your-fit')) {
  // How long the skeleton is allowed to wait on product imagery before the cards are
  // swapped in regardless — a slow CDN response must never leave the row shimmering.
  const IMAGE_SETTLE_TIMEOUT = 1500
  // Fallback for --cdcyf-per-view in component-cart-drawer-complete-your-fit.css.
  const DEFAULT_CARDS_PER_VIEW = 3
  const SCROLL_END_TOLERANCE = 1

  class CartDrawerCompleteYourFit extends HTMLElement {
    constructor() {
      super()
      this.updateScrollState = this.updateScrollState.bind(this)
      this.handleArrowClick = this.handleArrowClick.bind(this)
    }

    connectedCallback() {
      this.prevArrow = this.querySelector('[data-cyf-prev]')
      this.nextArrow = this.querySelector('[data-cyf-next]')
      this.prevArrow?.addEventListener('click', this.handleArrowClick)
      this.nextArrow?.addEventListener('click', this.handleArrowClick)
      this.render()
    }

    disconnectedCallback() {
      this.list?.removeEventListener('scroll', this.updateScrollState)
      window.removeEventListener('resize', this.updateScrollState)
      this.prevArrow?.removeEventListener('click', this.handleArrowClick)
      this.nextArrow?.removeEventListener('click', this.handleArrowClick)
      this.list = null
    }

    parseProducts() {
      const script = this.querySelector('[cart-drawer-complete-your-fit-json-products]')
      if (!script) return []
      try {
        const products = JSON.parse(script.textContent || '[]')
        return Array.isArray(products) ? products.filter((product) => product?.id && product?.url) : []
      } catch (error) {
        console.warn('CartDrawerCompleteYourFit....unable to parse products', error)
        return []
      }
    }

    // The whole card is wrapped by the same <quick-add-button> used on the PDP carousel,
    // so image, title, price and the plus button all open one shared quick-add modal.
    createCard(product, index) {
      const item = document.createElement('li')
      item.className = product.is_clearance ? 'cdcyf__item cdcyf__item--clearance' : 'cdcyf__item'

      const quickAdd = this.createQuickAddButton(product)

      const mediaWrapper = document.createElement('div')
      mediaWrapper.className = 'cdcyf__media-wrapper'

      const media = document.createElement('div')
      media.className = 'cdcyf__media'

      if (product.image) {
        const image = document.createElement('img')
        image.src = product.image
        image.alt = product.image_alt || ''
        // The four cards in view are eager so they can decode while still detached
        // (a lazy image never loads off-document, which is what waitForImages awaits);
        // everything further along the carousel stays lazy.
        image.loading = index < DEFAULT_CARDS_PER_VIEW ? 'eager' : 'lazy'
        media.appendChild(image)
      }
      mediaWrapper.appendChild(media)
      mediaWrapper.appendChild(this.createQuickAddTrigger(product))
      quickAdd.appendChild(mediaWrapper)

      const heading = document.createElement('h4')
      const title = document.createElement('span')
      // product-title (via the JSON snippet) emits non-breaking hyphens as an HTML
      // entity, so this is Liquid-rendered markup rather than plain text.
      title.innerHTML = product.title_html || product.plain_title || ''
      heading.appendChild(title)
      quickAdd.appendChild(heading)

      quickAdd.appendChild(this.createPrice(product))
      item.appendChild(quickAdd)
      return item
    }

    // A real <quick-add-button> element (quick-add-button.js is loaded site-wide from
    // theme.liquid), so every click inside the card uses the global quick-add flow.
    createQuickAddButton(product) {
      const quickAdd = document.createElement('quick-add-button')
      quickAdd.className = 'cdcyf__card-trigger'
      quickAdd.dataset.productHref = product.url
      quickAdd.dataset.pageType = this.dataset.pageType || 'other'
      quickAdd.dataset.productFormattedPrice = product.price || ''
      quickAdd.dataset.currency = this.dataset.currency || ''
      quickAdd.dataset.productId = product.id
      quickAdd.dataset.productVendor = product.vendor || ''
      quickAdd.dataset.productType = product.type || ''
      quickAdd.dataset.productTitle = product.plain_title || ''
      // Every card here is a single, so the modal opens without its quantity control —
      // matching the PDP style-it-with carousel.
      quickAdd.dataset.hideQuantitySelector = 'true'
      // Clearance cards are priced at the clearance tier, so the modal must only offer
      // the clearance variants that price applies to.
      if (product.is_clearance) quickAdd.dataset.showOnlyClearance = 'true'

      return quickAdd
    }

    createQuickAddTrigger(product) {
      const trigger = document.createElement('span')
      trigger.className = 'pwd-quick-add__button cdcyf__quick-add'

      const button = document.createElement('button')
      button.className = 'addButton'
      button.type = 'button'
      button.setAttribute('aria-label', product.quick_add_label || '')
      trigger.appendChild(button)
      return trigger
    }

    createPrice(product) {
      const price = document.createElement('div')
      price.className = 'cdcyf__price'

      if (product.compare_at_price) {
        const regularLabel = document.createElement('span')
        regularLabel.className = 'visually-hidden'
        regularLabel.textContent = this.dataset.labelRegularPrice || ''
        price.appendChild(regularLabel)

        const compareAt = document.createElement('s')
        compareAt.textContent = product.compare_at_price
        price.appendChild(compareAt)

        const discountedLabel = document.createElement('span')
        discountedLabel.className = 'visually-hidden'
        discountedLabel.textContent = this.dataset.labelDiscountedPrice || ''
        price.appendChild(discountedLabel)
      }

      const finalPrice = document.createElement('span')
      finalPrice.className = 'cdcyf__price-final'
      finalPrice.textContent = product.price || ''
      price.appendChild(finalPrice)

      // Percent-off badge for whatever marked the card down — compare-at, promo,
      // weekly deal, or clearance tier — matching the badge pdp-style-it-with renders
      // on the PDP carousel. Null when the card is at full price.
      if (product.discount_label) {
        const discount = document.createElement('span')
        discount.className = 'cdcyf__price-discount'
        discount.textContent = product.discount_label
        price.appendChild(discount)
      }

      return price
    }

    // Cards are built detached and only swapped in once their imagery has decoded, so
    // the skeleton hands over to a finished row instead of to blank grey boxes.
    waitForImages(cards) {
      const images = cards.flatMap((card) => Array.from(card.querySelectorAll('img[loading="eager"]')))
      if (!images.length) return Promise.resolve()

      const loaded = images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              image.addEventListener('load', resolve, { once: true })
              image.addEventListener('error', resolve, { once: true })
            })
      )
      const timeout = new Promise((resolve) => window.setTimeout(resolve, IMAGE_SETTLE_TIMEOUT))
      return Promise.race([Promise.all(loaded), timeout])
    }

    async render() {
      const list = this.querySelector('[data-cyf-list]')
      if (!list) return
      this.list = list

      const products = this.parseProducts()
      if (!products.length) {
        this.hidden = true
        return
      }

      const cards = products.map((product, index) => this.createCard(product, index))
      await this.waitForImages(cards)
      // The drawer re-renders this snippet on every cart change, which can retire this
      // element mid-wait; whatever replaced it is already rendering its own cards.
      if (!this.isConnected) return

      list.replaceChildren(...cards)
      list.removeAttribute('aria-busy')
      this.classList.add('cdcyf--ready')

      list.addEventListener('scroll', this.updateScrollState, { passive: true })
      window.addEventListener('resize', this.updateScrollState)
      this.updateScrollState()
    }

    handleArrowClick(event) {
      this.scrollByCards(event.currentTarget.hasAttribute('data-cyf-next') ? 1 : -1)
    }

    // Match the PDP carousel: each press moves one view and lands on a card edge.
    scrollByCards(direction) {
      const card = this.list?.querySelector('.cdcyf__item')
      if (!card) return

      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0
      const cardsPerView =
        Number.parseFloat(getComputedStyle(this).getPropertyValue('--cdcyf-per-view')) ||
        DEFAULT_CARDS_PER_VIEW
      const step = (card.getBoundingClientRect().width + gap) * cardsPerView
      this.list.scrollBy({ left: direction * step, behavior: 'smooth' })
    }

    updateScrollState() {
      if (!this.list || !this.prevArrow || !this.nextArrow) return

      const maxScroll = this.list.scrollWidth - this.list.clientWidth
      this.querySelector('.cdcyf__arrows')?.toggleAttribute(
        'hidden',
        maxScroll <= SCROLL_END_TOLERANCE,
      )
      this.toggleArrow(this.prevArrow, this.list.scrollLeft <= SCROLL_END_TOLERANCE)
      this.toggleArrow(
        this.nextArrow,
        maxScroll <= SCROLL_END_TOLERANCE ||
          this.list.scrollLeft >= maxScroll - SCROLL_END_TOLERANCE,
      )
    }

    toggleArrow(arrow, isDisabled) {
      arrow.classList.toggle('cdcyf__arrow--disabled', isDisabled)
      arrow.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')
    }
  }

  customElements.define('cart-drawer-complete-your-fit', CartDrawerCompleteYourFit)
}
